import { ApolloClient, ApolloLink, concat, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { useWeb3React } from '@web3-react/core'
import { SupportedChainId } from 'constants/chains'
import { useMemo, useRef } from 'react'

import store, { AppState } from '../../state/index'

const CHAIN_SUBGRAPH_URL: Record<number, string> = {}

// V3 Staker subgraph endpoint
const V3_STAKER_SUBGRAPH_URL: Record<number, string> = {
  [SupportedChainId.HYDRA]: 'https://subgraph.hydrachain.org/subgraphs/name/v3-staker',
  [SupportedChainId.TESTNET]: 'https://subgraph.hydrachain.org/testnet/subgraphs/name/v3-staker',
}

const httpLink = new HttpLink({ uri: CHAIN_SUBGRAPH_URL[SupportedChainId.HYDRA] })

// This middleware will allow us to dynamically update the uri for the requests based off chainId
// For more information: https://www.apollographql.com/docs/react/networking/advanced-http-networking/
const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const chainId = (store.getState() as AppState).application.chainId

  operation.setContext(() => ({
    uri:
      chainId && CHAIN_SUBGRAPH_URL[chainId] ? CHAIN_SUBGRAPH_URL[chainId] : CHAIN_SUBGRAPH_URL[SupportedChainId.HYDRA],
  }))

  return forward(operation)
})

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: concat(authMiddleware, httpLink),
})

// Stable cache shared across chain switches — prevents LM campaign data loss
const v3StakerCache = new InMemoryCache()

export function useV3StakerApolloClient(): ApolloClient<NormalizedCacheObject> {
  const { chainId } = useWeb3React()
  const clientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null)
  const prevChainIdRef = useRef<number | undefined>(undefined)

  return useMemo(() => {
    const uri = V3_STAKER_SUBGRAPH_URL[chainId ?? SupportedChainId.HYDRA]

    if (!clientRef.current || prevChainIdRef.current !== chainId) {
      // Clear cache on chain switch (data is chain-specific), but keep the client stable
      if (prevChainIdRef.current !== undefined && prevChainIdRef.current !== chainId) {
        v3StakerCache.reset()
      }
      prevChainIdRef.current = chainId

      clientRef.current = new ApolloClient({
        cache: v3StakerCache,
        link: new HttpLink({ uri }),
      })
    }

    return clientRef.current
  }, [chainId])
}
